import {
    DndContext,
    DragOverlay,
    PointerSensor,
    closestCorners,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { formatCurrencyInCents, presentUuid } from '../../helpers/formatters';
import { applyEdges, applyName } from '../../helpers/captureMode';
import { salesChannelKanbanLabel } from '../../helpers/salesChannels';
import {
    KANBAN_COLUMNS,
    KANBAN_COMPLETE_ZONE,
    canCompleteOrder,
    kanbanColumnLabel,
    kanbanMoveSteps,
    orderKanbanColumn,
} from '../../helpers/orderStages';

function OrderCardBody({ order }) {
    return (
        <>
            <Typography variant="body2" fontWeight={600}>
                #{presentUuid(order.id)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
                {applyName(order.customer?.name) || 'Sem cliente'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-word' }}>
                {salesChannelKanbanLabel(
                    order.sales_channel?.name,
                    applyEdges(order.external_order_id),
                )}
            </Typography>
            <Typography variant="caption" display="block">
                {formatCurrencyInCents(order.amount)}
            </Typography>
        </>
    );
}

function KanbanCard({ order, onOpen, onEdit, onComplete }) {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: order.id,
        data: { order },
    });

    return (
        <Card
            ref={setNodeRef}
            variant="outlined"
            sx={{
                cursor: 'grab',
                flexShrink: 0,
                overflow: 'visible',
                opacity: isDragging ? 0.35 : 1,
                transform: CSS.Translate.toString(transform),
            }}
            {...listeners}
            {...attributes}
        >
            <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Box onClick={() => onOpen?.(order.id)} sx={{ cursor: 'pointer' }}>
                    <OrderCardBody order={order} />
                </Box>
                <Stack direction="row" spacing={0.75} sx={{ mt: 1.25 }} useFlexGap flexWrap="wrap">
                    <Button
                        size="small"
                        variant="outlined"
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={() => onEdit?.(order.id)}
                    >
                        Editar
                    </Button>
                    {canCompleteOrder(order) ? (
                        <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onPointerDown={(event) => event.stopPropagation()}
                            onClick={() => onComplete?.(order)}
                        >
                            Concluir
                        </Button>
                    ) : null}
                </Stack>
            </CardContent>
        </Card>
    );
}

function KanbanColumn({ column, cards, onOpen, onEdit, onComplete }) {
    const { setNodeRef, isOver } = useDroppable({ id: column.id });

    return (
        <Box
            ref={setNodeRef}
            sx={{
                minWidth: 272,
                flex: '0 0 272px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: 1,
                borderRadius: 1,
                bgcolor: isOver ? 'action.selected' : 'action.hover',
                minHeight: 0,
            }}
        >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, flexShrink: 0 }}>
                {kanbanColumnLabel(column.id)}
                {' '}
                ({cards.length})
            </Typography>
            <Stack spacing={1} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5, '& > *': { flexShrink: 0 } }}>
                {cards.map((order) => (
                    <KanbanCard
                        key={order.id}
                        order={order}
                        onOpen={onOpen}
                        onEdit={onEdit}
                        onComplete={onComplete}
                    />
                ))}
            </Stack>
        </Box>
    );
}

function CompleteDropZone() {
    const { setNodeRef, isOver } = useDroppable({ id: KANBAN_COMPLETE_ZONE });

    return (
        <Box
            ref={setNodeRef}
            sx={{
                minWidth: 220,
                flex: '0 0 220px',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                borderRadius: 1,
                border: '2px dashed',
                borderColor: isOver ? 'success.main' : 'success.light',
                bgcolor: isOver ? 'success.main' : 'transparent',
                color: isOver ? 'success.contrastText' : 'success.main',
                transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
            }}
        >
            <Typography
                variant="body2"
                sx={{
                    textAlign: 'center',
                    fontWeight: 700,
                    lineHeight: 1.45,
                }}
            >
                Pedidos entregues podem ser arrastados para cá ou concluídos pelo botão Concluir
            </Typography>
        </Box>
    );
}

function ColumnGroup({ title, columns, cardsByColumn, onOpen, onEdit, onComplete, trailing }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, px: 1, flexShrink: 0, mb: 0.5 }}>
                {title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minHeight: 0, alignItems: 'stretch' }}>
                {columns.map((column) => (
                    <KanbanColumn
                        key={column.id}
                        column={column}
                        cards={cardsByColumn[column.id] || []}
                        onOpen={onOpen}
                        onEdit={onEdit}
                        onComplete={onComplete}
                    />
                ))}
                {trailing}
            </Box>
        </Box>
    );
}

export default function OrderKanban({ orders, onOpen, onEdit, onMove, onComplete }) {
    const [activeOrder, setActiveOrder] = useState(null);
    const [saving, setSaving] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    );

    const cardsByColumn = useMemo(() => {
        const grouped = {};
        KANBAN_COLUMNS.forEach((column) => {
            grouped[column.id] = [];
        });
        (orders || []).forEach((order) => {
            const columnId = orderKanbanColumn(order);
            if (grouped[columnId]) {
                grouped[columnId].push(order);
            }
        });
        return grouped;
    }, [orders]);

    const productionColumns = KANBAN_COLUMNS.filter((column) => column.group === 'production');
    const logisticsColumns = KANBAN_COLUMNS.filter((column) => column.group === 'logistics');

    const handleDragStart = (event) => {
        setActiveOrder(event.active.data.current?.order || null);
    };

    const handleDragEnd = async (event) => {
        const order = event.active.data.current?.order;
        const nextColumn = event.over?.id ? String(event.over.id) : '';
        setActiveOrder(null);
        if (!order) {
            return;
        }
        if (nextColumn === KANBAN_COMPLETE_ZONE) {
            if (!canCompleteOrder(order)) {
                Swal.fire(
                    'Ainda não dá para concluir',
                    order.logistics_status === 'delivered'
                        ? 'Confirme o pagamento do cliente antes de concluir o pedido.'
                        : 'Só pedidos entregues podem ser concluídos por aqui. Não entregue e devolvido ficam na lista e na edição.',
                    'info',
                );
                return;
            }
            setSaving(true);
            try {
                await onComplete?.(order);
            } finally {
                setSaving(false);
            }
            return;
        }
        const steps = kanbanMoveSteps(order, nextColumn);
        if (steps.length === 0 || !onMove) {
            return;
        }
        setSaving(true);
        try {
            await onMove(order, steps);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, flexShrink: 0 }}>
                Arraste o card para evoluir. Pedido entregue pode ser solto na faixa verde ou concluído pelo botão Concluir.
                {saving ? ' Salvando…' : ''}
            </Typography>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={() => setActiveOrder(null)}
            >
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        flex: 1,
                        minHeight: 0,
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        alignItems: 'stretch',
                        pb: 1,
                    }}
                >
                    <ColumnGroup
                        title="Produção"
                        columns={productionColumns}
                        cardsByColumn={cardsByColumn}
                        onOpen={onOpen}
                        onEdit={onEdit}
                        onComplete={onComplete}
                    />
                    <Box sx={{ alignSelf: 'stretch', borderLeft: '1px dashed', borderColor: 'divider', mx: 0.5 }} />
                    <ColumnGroup
                        title="Logística"
                        columns={logisticsColumns}
                        cardsByColumn={cardsByColumn}
                        onOpen={onOpen}
                        onEdit={onEdit}
                        onComplete={onComplete}
                        trailing={<CompleteDropZone />}
                    />
                </Box>
                <DragOverlay>
                    {activeOrder ? (
                        <Card variant="outlined" sx={{ width: 256, boxShadow: 6 }}>
                            <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                                <OrderCardBody order={activeOrder} />
                            </CardContent>
                        </Card>
                    ) : null}
                </DragOverlay>
            </DndContext>
        </Box>
    );
}
